// Global
import { sendGTMEvent } from '@next/third-parties/google';
import {
  Link as JSSLink,
  LinkProps,
  LinkField,
  LinkFieldValue,
} from '@sitecore-content-sdk/nextjs';
import NextLink from 'next/link';
import React, { JSX, forwardRef } from 'react';
import { tv } from 'tailwind-variants';

// Local
import { CtaProps, ctaTailwindVariant } from 'helpers/SitecoreWrappers/ButtonWrapper/ButtonWrapper';
import { SvgIcon } from 'helpers/SvgIcon';
import useIsEditing from 'lib/hooks/useIsEditing';
import { GtmEvent } from 'lib/utils/gtm-utils';
import { useFieldWithFallbacks } from 'lib/hooks/useFieldWithFallbacks';
import { CtaIcons } from 'lib/utils/style-param-utils/modules/ctas';
import { parseLink } from 'lib/utils/link-utils';
import classNames from 'classnames';
import { useAppointmentModal } from 'helpers/Context/AppointmentModalProvider';
import { UrlObject } from 'node:url';
import { UrlWithRelativePath } from 'lib/utils/string-utils';

const INTERNAL_LINK_REGEX = /^\/|^\#/g;

export type LinkWrapperProps = Omit<LinkProps, 'field' | 'href'> &
  CtaProps & {
    field?: LinkField | LinkFieldValue;
    fallbacks?: (LinkField | LinkFieldValue | undefined)[];
    gtmEvent?: GtmEvent;
    srOnlyText?: string;
    suppressNewTabIcon?: boolean;
    locale?: string;
    isHashLink?: boolean;
  };

const linkWrapperTailwindVariant = tv({
  extend: ctaTailwindVariant,
  slots: {
    iconNewTab: ['align-middle', 'inline-flex', 'ml-2', '-mt-1'],
  },
});

const LinkWrapper = forwardRef<HTMLAnchorElement, LinkWrapperProps>(
  (originalProps: LinkWrapperProps, ref): JSX.Element | null => {
    const {
      onClick,
      children,
      className,
      ctaStyle,
      ctaIcon: ctaIconOverride,
      ctaIconAlignment: ctaIconAlignmentOverride,
      ctaVariant: ctaVariantOverride,
      ctaVisibility: ctaVisibilityOverride,
      editable = true,
      field,
      fallbacks,
      gtmEvent,
      showLinkTextWithChildrenPresent = false,
      srOnlyText,
      suppressNewTabIcon,
      ...props
    } = originalProps;

    const isEditing = useIsEditing() && editable;

    const { renderField } = useFieldWithFallbacks(field, fallbacks);

    const { open } = useAppointmentModal();

    // If there's no field, don't render anything. This should only be the case
    // if the field itself is undefined, which should only happen if code is deployed
    // before data templates are published, so this is just a safety check
    if (!renderField) {
      return <></>;
    }

    const isModalLink =
      ((renderField as LinkField)?.value?.linktype === 'internal' &&
        (renderField as LinkField)?.value?.querystring?.includes('openSchedulingItemId')) ||
      ((renderField as LinkField)?.value?.linktype === 'external' &&
        (renderField as LinkField)?.value?.href?.includes('openSchedulingItemId'));

    let openSchedulingItemId: string | null = null;
    if (isModalLink) {
      let params: URLSearchParams | null = null;
      if ((renderField as LinkField)?.value?.querystring) {
        params = getParamsFromURI((renderField as LinkField)?.value?.querystring || '');
      } else {
        params = getParamsFromURI((renderField as LinkField)?.value?.href || '');
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      openSchedulingItemId = params?.get('openSchedulingItemId') || null;
    }

    const ctaIcon = ctaIconOverride ?? ctaStyle?.ctaIcon;
    const ctaVariant = ctaVariantOverride ?? ctaStyle?.ctaVariant ?? 'link';
    const ctaIconAlignment = ctaIconAlignmentOverride ?? ctaStyle?.ctaIconAlignment ?? 'right';
    const ctaVisibility = ctaVisibilityOverride ?? ctaStyle?.ctaVisibility ?? 'visible';

    // Clone the object so we don't modify the original.
    // This addresses some edge cases issues when the same link is rendered more than once
    // and we're modifying the link.  While it may not always be needed, it's safer to include
    const clonedField = structuredClone(renderField);

    // Standardize the field because it can either be LinkField or LinkFieldValue
    const fieldValue: LinkFieldValue = {
      ...((clonedField as LinkField)?.value ?? (clonedField as LinkFieldValue)),
    };

    const { target, title } = fieldValue;

    const { realText, shouldRender, isInternalAnchor, isCustomProtocol, parsedUrl } = parseLink(
      fieldValue,
      children,
      showLinkTextWithChildrenPresent
    );

    /*
     * RENDERING
     */

    const { base } = linkWrapperTailwindVariant({
      iconAlignment: ctaIconAlignment,
      variant: ctaVariant,
      visibility: ctaVisibility,
    });

    // If editing, always render
    if (isEditing) {
      return (
        <EditModeLink
          {...originalProps}
          {...{ ctaIcon, ctaIconAlignment, ctaVariant, ctaVisibility }}
          ref={ref}
          field={clonedField}
        />
      );
    }

    // if (!shouldRender || !parsedUrl) {
    //   return <></>;
    // }

    // As per project requirement, Show only tertiary if no URL, hide other variants like Primary, secondary and secondarysmall
    if (!shouldRender || !parsedUrl) {
      if (ctaVariant.toLocaleLowerCase() === 'tertiary') {
        const fallbackText = fieldValue?.text || title || '';
        return (
          <span
            className={classNames(base(), '!text-gray-900 !no-underline', className)}
            data-component="helpers/sitecorewrappers/linkwrapper"
          >
            {fallbackText && <span>{fallbackText}</span>}
            {children}
          </span>
        );
      }

      return <></>;
    }

    /*
     * EVENT HANDLERS
     */

    const handleOnClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      if (renderField?.value) {
        const gtmEventInner = {
          ...gtmEvent,
          'gtm.element.dataset.gtmLinkName': realText || title,
          'gtm.element.dataset.gtmLinkUrl': parsedUrl.href,
        };

        sendGTMEvent(gtmEventInner);
      }

      if (isModalLink && openSchedulingItemId) {
        open(openSchedulingItemId);
        e.preventDefault();
      }

      // Scroll with offset - this is additional code to fix redirect with anchor link for example: /find-care#SectionName
      if (parsedUrl.hash && !originalProps.isHashLink) {
        e.preventDefault(); // prevent default Next.js scroll
        const hrefWithoutHash = parsedUrl.pathname || parsedUrl.href.split('#')[0];

        import('next/router').then(({ default: router }) => {
          router.push(hrefWithoutHash).then(() => {
            // Use requestAnimationFrame to wait until DOM is ready
            const scrollToHash = () => {
              const el = document.querySelector(parsedUrl.hash!);
              if (el) {
                const headerOffset = 80; // adjust for sticky header if needed
                const elementPosition = el.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - headerOffset;
                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
              }
            };

            // Try multiple frames if component above loads late
            const tryScroll = (count = 0) => {
              scrollToHash();
              if (count < 5) {
                requestAnimationFrame(() => tryScroll(count + 1));
              }
            };

            tryScroll();
          });
        });
        return;
      }

      if (onClick) onClick(e);
    };

    /*
     * Next/Link doesn't handle named anchors. At all. In the event that the user has defined a named anchor
     * (i.e. anchor has a value, href has no value, and linktype is "internal"),
     * then use a standard anchor tag to render the link.
     */
    if (isInternalAnchor || isCustomProtocol) {
      return (
        <a
          {...props}
          className={[className, base()].join(' ')}
          data-component="helpers/sitecorewrappers/linkwrapper"
          href={isInternalAnchor ? parsedUrl.hash : parsedUrl.href}
          onClick={handleOnClick}
          ref={ref}
          title={title || realText}
          aria-label={props['aria-label'] ? props['aria-label'] : realText ? realText : srOnlyText}
        >
          {!children ? (
            <span>
              {realText ||
                (parsedUrl.href?.startsWith('/') ? parsedUrl.href.slice(1) : parsedUrl.href)}
            </span>
          ) : (
            <span>{realText}</span>
          )}
          {children}
          <ScreenReaderOnlyTextAndNewTabIcon
            target={target}
            srOnlyText={srOnlyText}
            suppressNewTabIcon={suppressNewTabIcon}
            ctaIcon={ctaIcon}
          />
        </a>
      );
    }

    /*
    ToDo: The code for urls is mistyped currently, and the class UrlWithRelativePath is being used
    as a type when a variety of different URL objects are passed in, not always from new UrlWithRelativePath(..)
    For external links, use the current system, else parse out the properties we need for an internal link,
     avoiding passing extra properties for the class that are not part of NextLink's href property
    */
    const url = parsedUrl?.protocol ? parsedUrl : parsedUrlToNodeUrl(parsedUrl);

    return (
      <NextLink
        {...props}
        className={[className, base()].join(' ')}
        data-component="helpers/sitecorewrappers/linkwrapper"
        href={url}
        onClick={handleOnClick}
        prefetch={false}
        ref={ref}
        target={target}
        title={title || realText}
        aria-label={props['aria-label'] ? props['aria-label'] : realText ? realText : srOnlyText}
      >
        {realText ? <span>{realText}</span> : null}
        {children}

        <ScreenReaderOnlyTextAndNewTabIcon
          target={target}
          srOnlyText={srOnlyText}
          suppressNewTabIcon={suppressNewTabIcon}
          ctaIcon={ctaIcon}
        />
      </NextLink>
    );
  }
);

LinkWrapper.displayName = 'LinkWrapper';

export default LinkWrapper;

function ScreenReaderOnlyTextAndNewTabIcon({
  srOnlyText,
  suppressNewTabIcon,
  target,
  ctaIcon,
}: {
  srOnlyText?: string;
  suppressNewTabIcon?: boolean;
  target?: string;
  ctaIcon?: CtaIcons;
}) {
  if (target !== '_blank' && !srOnlyText && !ctaIcon) {
    return <></>;
  }

  const { icon, iconNewTab } = linkWrapperTailwindVariant();

  const Icon = ctaIcon ? (
    <SvgIcon className={icon()} icon={ctaIcon} size="lg" />
  ) : !suppressNewTabIcon && target === '_blank' ? (
    <SvgIcon className={iconNewTab()} icon="new-tab" size="lg" />
  ) : null;

  return (
    <>
      {Icon}

      {srOnlyText ? (
        <span className="sr-only">
          {/* Preserve a single space character before SR Tab Text */}
          {`${srOnlyText ? srOnlyText : ''}${target === '_blank' ? ' (Opens in a new tab)' : ''}`}
        </span>
      ) : null}
    </>
  );
}

const EditModeLink = forwardRef<HTMLAnchorElement, LinkWrapperProps>(
  (
    { ctaIconAlignment, ctaVariant, ctaVisibility, ctaIcon, className, field, children, ...props },
    ref
  ) => {
    const { base, icon } = linkWrapperTailwindVariant({
      iconAlignment: ctaIconAlignment,
      variant: ctaVariant,
      visibility: ctaVisibility,
    });
    const editableClonedField = field?.value ? (field as LinkField) : { value: { ...field } };

    // The Link component, in its editable configuration, doesn't handle internal links correctly; i.e. it won't render the link text without a value for href.
    // For internal links, we'll forcibly set the href to '/' to work around the issue in a way that doesn't otherwise affect the editing experience.
    if (
      editableClonedField?.value?.linktype === 'internal' &&
      editableClonedField.value.href === ''
    )
      editableClonedField.value.href = '/';

    return (
      // Adding the CSS classes to a wrapping div so we can include the icon
      <div className={[className, base()].join(' ')}>
        <JSSLink
          {...props}
          field={editableClonedField}
          // className={`${title ? 'flex items-center' : ''}`}
          internalLinkMatcher={INTERNAL_LINK_REGEX}
          ref={ref}
          aria-label={props['aria-label'] ? props['aria-label'] : editableClonedField.value.text}
        >
          {children}
        </JSSLink>
        {/* When in edit mode we cannot render anything inside the Link tag (cause duplicate link), 
          but we can rendering it outside of the link and move the styling to a parent div */}
        {ctaIcon && <SvgIcon className={icon()} icon={ctaIcon} size="xs" />}
      </div>
    );
  }
);

EditModeLink.displayName = 'EditModeLink';

const getParamsFromURI = (uri: string) => {
  // If it's a full URL, use URL to get the search part
  try {
    if (uri.startsWith('http') || uri.startsWith('https')) {
      const url = new URL(uri);
      return new URLSearchParams(url.search);
    }
  } catch (_e) {
    // Not a valid URL, fall through
    console.warn('uri not valid');
  }
  // If it starts with '?', remove it
  const paramString = uri.startsWith('?') ? uri.slice(1) : uri;
  return new URLSearchParams(paramString);
};

function parsedUrlToNodeUrl(relativeUrl: UrlWithRelativePath): UrlObject {
  return {
    pathname: relativeUrl.pathname,
    hash: relativeUrl.hash,
    search: relativeUrl.search,
  };
}
